<template>
  <div class="flex justify-between items-center color-primary bg-base mt-3 pa-2 rounded-t-lg">
    <div class="flex items-center gap-2 flex-1 font-700">
      <CCheckbox
        v-if="!loading && totalItems > 0"
        v-tooltip="
          areAllSelected
            ? t('canvasUI.components.CBulkToolbar.deselectAll')
            : t('canvasUI.components.CBulkToolbar.selectAll')
        "
        :checked="areAllSelected"
        class="mx-2"
        :disabled="loading"
        :indeterminate="areSomeSelected"
        :label="
          t('canvasUI.canvasBuilder.sectionSelector.countResults', {
            totalCount: totalItems,
            start: (currentPage - 1) * pageSize + 1,
            end:
              currentPage === 1 && firstPageTotalDisplayedItems
                ? firstPageTotalDisplayedItems
                : Math.min(currentPage * pageSize, totalItems),
          })
        "
        @click.stop="emit('toggle-select-all')"
      />
      <CPaginationButtons
        v-if="launchDarkly.enable_selectors_dnd"
        :current-page="currentPage"
        :disable-pagination="disablePagination"
        :loading="loading"
        :page-size="pageSize"
        :total-items="totalItems"
        @page-first="emit('page-first')"
        @page-last="emit('page-last')"
        @page-next="emit('page-next')"
        @page-previous="emit('page-previous')"
      />
    </div>
    <CPaginationButtons
      v-if="!launchDarkly.enable_selectors_dnd"
      :current-page="currentPage"
      :disable-pagination="disablePagination"
      :loading="loading"
      :page-size="pageSize"
      :total-items="totalItems"
      @page-first="emit('page-first')"
      @page-last="emit('page-last')"
      @page-next="emit('page-next')"
      @page-previous="emit('page-previous')"
    />
    <CSectionSelectionActions
      :added-item-ids="addedItemIds"
      :selected-item-ids="selectedItemIds"
      :total-selected="totalSelected"
      @clear-selection="emit('clear-selection')"
      @items-added="(itemIds, position) => emit('items-added', itemIds, position)"
      @items-removed="(itemIds) => emit('items-removed', itemIds)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CCheckbox from '@lib/components/CCheckbox/CCheckbox.vue'
import CPaginationButtons from '@lib/components/CPaginationButtons/CPaginationButtons.vue'
import CSectionSelectionActions from '@lib/components/CSectionSelectionActions/CSectionSelectionActions.vue'

import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CanvasRetrieve } from '@openapi'

withDefaults(
  defineProps<{
    currentPage: number
    disablePagination?: boolean
    firstPageTotalDisplayedItems: number
    loading?: boolean
    pageSize: number
    totalItems: number
    totalSelected: number
    areAllSelected: boolean
    areSomeSelected: boolean
    addedItemIds?: CanvasRetrieve['id'][]
    selectedItemIds?: CanvasRetrieve['id'][]
  }>(),
  {
    addedItemIds: () => [],
    selectedItemIds: () => [],
    disablePagination: false,
    loading: false,
  },
)

const emit = defineEmits<{
  (e: 'page-first'): void
  (e: 'page-last'): void
  (e: 'page-next'): void
  (e: 'page-previous'): void
  (e: 'toggle-select-all'): void
  (e: 'clear-selection'): void
  (e: 'items-added', itemIds: CanvasRetrieve['id'][], position?: 'top' | 'bottom'): void
  (e: 'items-removed', itemIds: CanvasRetrieve['id'][]): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const { t } = useI18n()
</script>
